import React, { useEffect, useState, useContext, useMemo } from "react";
import axios from "../context/AxiosInstance";
import { Table, Button, Form, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import Navbar from "../components/Navbar";
import AppBreadcrumb from "../components/AppBreadcrumb";
import Sidebar from "../components/Sidebar";
import CrgFormModal from "../components/crg/CrgFormModal";
import ConfirmDeleteModal from "../components/crg/ConfirmDeleteModal";
import EmptyState from "../components/common/EmptyState";
import RhKpiCard from "../components/common/RhKpiCard";
import RhTablePagination from "../components/common/RhTablePagination";
import { FaCoins, FaSearch, FaPlus, FaPen, FaTrash } from "react-icons/fa";
import "../styles/rh-modern-ui.css";

const CrgPage = () => {
  const { token, role } = useContext(AuthContext);
  const navigate = useNavigate();

  const [crgs, setCrgs] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const [showFormModal, setShowFormModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedCrg, setSelectedCrg] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [loading, setLoading] = useState(true);

  const isAllowed = role === "RH" || role === "Assistant" || role === "ASSISTANT";

  const fetchCrgs = async () => {
    setLoading(true);
    try {
      const res = await axios.get("/crg/", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCrgs(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (err) {
      if (err.response?.status === 401 || !token) {
        navigate("/login");
      } else {
        console.error("Erreur de chargement :", err);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token && isAllowed) fetchCrgs();
  }, [token, role]);

  const filtered = crgs.filter((c) =>
    `${c.code_crg} ${c.libelle_crg} ${c.prise_en_charge_crg} ${c.type_crg}`
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  const kpis = useMemo(() => {
    const list = crgs || [];
    const cotisations = list.filter((c) => (c.type_crg || "").toLowerCase().includes("cotisation")).length;
    const retenues = list.filter((c) => (c.type_crg || "").toLowerCase().includes("retenue")).length;
    const gains = list.filter((c) => (c.type_crg || "").toLowerCase().includes("gain")).length;
    const types = new Set(list.map((c) => c.type_crg).filter(Boolean)).size;
    return { total: list.length, cotisations, retenues, gains, types };
  }, [crgs]);

  const currentData = filtered.slice((currentPage - 1) * perPage, currentPage * perPage);

  const openForm = (c = null) => {
    setSelectedCrg(c);
    setIsEditMode(!!c);
    setShowFormModal(true);
  };

  const openDelete = (c) => {
    setSelectedCrg(c);
    setShowDeleteModal(true);
  };

  return (
    <div className="d-flex">
      <Sidebar />
      <div className="main-content w-100">
        <Navbar />
        <div className="container-fluid p-4 p-lg-5 rh-page-content">
          <AppBreadcrumb items={[{ label: "Accueil", to: "/" }, { label: "CRG" }]} />

          <header className="rh-page-header">
            <h4>
              <span className="rh-page-icon"><FaCoins /></span>
              Cotisations, Retenues &amp; Gains
            </h4>
            <p>Référentiel des rubriques de paie : codes, libellés et types de prise en charge.</p>
          </header>

          {!isAllowed ? (
            <div className="rh-alert-unauth">
              Accès refusé : seuls les utilisateurs RH et Assistant peuvent gérer les CRG.
            </div>
          ) : loading ? (
            <div className="rh-loading-block">
              <Spinner animation="border" />
              <p className="mt-3 mb-0">Chargement en cours…</p>
            </div>
          ) : (
            <>
              <div className="rh-kpi-grid">
                <RhKpiCard label="Total rubriques" value={kpis.total} variant="primary" />
                <RhKpiCard label="Cotisations" value={kpis.cotisations} variant="info" />
                <RhKpiCard label="Retenues" value={kpis.retenues} variant="warning" />
                <RhKpiCard label="Gains" value={kpis.gains} variant="success" />
                <RhKpiCard label="Types distincts" value={kpis.types} variant="neutral" />
              </div>

              <div className="rh-toolbar">
                <div className="rh-toolbar-left">
                  <div className="rh-search-wrap">
                    <FaSearch className="rh-search-icon" />
                    <Form.Control
                      type="text"
                      placeholder="Code, libellé, prise en charge ou type…"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                  <span className="rh-result-pill">{filtered.length} résultat{filtered.length !== 1 ? "s" : ""}</span>
                </div>
                <div className="rh-toolbar-actions">
                  <Button className="rh-btn-primary" onClick={() => openForm(null)}>
                    <FaPlus /> Ajouter
                  </Button>
                </div>
              </div>

              <div className="rh-table-panel">
                {currentData.length > 0 ? (
                  <>
                    <div className="table-responsive">
                      <Table hover className="rh-modern-table mb-0">
                        <thead>
                          <tr>
                            <th>Code</th>
                            <th>Libellé</th>
                            <th>Prise en charge</th>
                            <th>Type</th>
                            <th className="text-center">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {currentData.map((c) => (
                            <tr key={c.id_crg}>
                              <td><span className="rh-badge rh-badge-indigo">{c.code_crg}</span></td>
                              <td>{c.libelle_crg}</td>
                              <td>{c.prise_en_charge_crg || "—"}</td>
                              <td><span className="rh-badge rh-badge-slate">{c.type_crg}</span></td>
                              <td>
                                <div className="rh-row-actions">
                                  {role === "RH" ? (
                                    <>
                                      <Button size="sm" variant="outline-success" className="rh-action-btn" onClick={() => openForm(c)}>
                                        <FaPen /> Modifier
                                      </Button>
                                      <Button size="sm" variant="outline-danger" className="rh-action-btn" onClick={() => openDelete(c)}>
                                        <FaTrash /> Supprimer
                                      </Button>
                                    </>
                                  ) : (
                                    <span className="text-muted small">Lecture seule</span>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                    <RhTablePagination
                      currentPage={currentPage}
                      totalItems={filtered.length}
                      perPage={perPage}
                      onPageChange={setCurrentPage}
                      onPerPageChange={(n) => { setPerPage(n); setCurrentPage(1); }}
                      itemLabel="rubriques"
                    />
                  </>
                ) : (
                  <div className="p-4">
                    <EmptyState title="Aucun CRG" description="Créez une cotisation, retenue ou gain pour commencer." />
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {isAllowed && (
          <>
            <CrgFormModal
              show={showFormModal}
              handleClose={() => setShowFormModal(false)}
              isEditMode={isEditMode}
              selectedCrg={selectedCrg}
              token={token}
              refreshData={fetchCrgs}
            />
            <ConfirmDeleteModal
              show={showDeleteModal}
              handleClose={() => setShowDeleteModal(false)}
              selectedItem={selectedCrg}
              token={token}
              refreshData={fetchCrgs}
              apiType="crg"
            />
          </>
        )}
      </div>
    </div>
  );
};

export default CrgPage;
